import React, { useState, useEffect } from "react";
import {
  User,
  Briefcase,
  FileText,
  Heart,
  BarChart2,
  Send,
  Loader,
  Star,
  TrendingUp,
  Eye,
  BadgeCheck,
} from "lucide-react";
import Button from "../../components/common/Button";
import { useNavigate } from "react-router-dom";
import authService from "../../api/authService";
import jobPostingApi from "../../api/jobPosting";
import ProfileCompletionSkeleton from "./components/ProfileCompletionSkeleton";
import EnhancedProfileCompletionCard from "./components/EnhancedProfileCompletionCard";
import QuickActionButton from "./components/QuickActionButton";
import StatCardSkeleton from "./components/StatCardSkeleton";
import StatCard from "./components/StatCard";
import JobCard from "./components/JobCard";
import ApplicationCard from "./components/ApplicationCard";
import professionalProfileApi from "../../api/professionalProfile";
import { toast } from "react-toastify";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/all";
import subscriptionApi from "../../api/subscription";
import SubscriptionStatus from "../../components/common/SubscriptionStatus";
import ExpiredLicenseBanner from "../../components/professional/ExpiredLicenseBanner";
import { useAuthStore } from "../../store/authStore";
import useSubscription from "../../hooks/useSubscription.js";

gsap.registerPlugin(ScrollTrigger);

const UserDashboard = () => {
  const user = useAuthStore(s => s.user);
  const profileCompletion = useAuthStore(s => s.profileCompletion());

  const navigate = useNavigate();
  const [profileLoading, setProfileLoading] = useState(true);
  const [recommendedJobs, setRecommendedJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [isCompletionDismissed, setIsCompletionDismissed] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  // const [subscriptionData, setSubscriptionData] = useState(null);
  const { data: subscriptionData} = useSubscription()
 
  useGSAP(() => {
    gsap.to(".welcome", {
      y: 0,
      opacity: 1,
      ease: "power2.inOut",
      duration: 1.5,
      stagger: 0.5,
      scrollTrigger: {
        trigger: ".welcome",
        toggleActions: "restart reverse restart reverse",
        start: "top 85%",
      },
    });
  }, []);

  useEffect(() => {
    const dismissedUntil = localStorage.getItem("completionMessageDismissedUntil");
    if (dismissedUntil && new Date().getTime() < parseInt(dismissedUntil, 10)) {
      setIsCompletionDismissed(true);
    }

    if (user?.role === "professional") {
      const fetchStats = async () => {
        setStatsLoading(true);
        try {
          const { data } = await authService.getApplicationStats();
          setStats(data);
        } catch {
          setStats({
            total_applications: 0,
            active_applications: 0,
            status_breakdown: {},
            recent_activity: [],
          });
        } finally {
          setStatsLoading(false);
        }
      };

      const fetchVerificationStatus = async () => {
        try {
          const response = await professionalProfileApi.getVerificationStatus();
          setVerificationData(response.data);
        } catch {
          setVerificationData({
            is_verified: false,
            verification_percentage: 0,
            education_verified: false,
            employment_verified: false,
            identity_verified: false,
            licenses_verified: false,
            pending_documents: [],
            verified_documents: [],
            rejected_documents: [],
          });
        }
      };

      const fetchSubscription = async () => {
        try {
          const res = await subscriptionApi.getMySubscription();
          setSubscriptionData(res.data);
        } catch {
          toast.error("Failed to fetch subscription details");
          setSubscriptionData(null);
        }
      };

      // ✅ profile_completeness now comes from /api/users/me/ via Zustand.
      // No need to fetchProfile() + calculateProfileCompletion() anymore.
      setProfileLoading(false);

      fetchStats();
      fetchVerificationStatus();
      // fetchSubscription();
    } else {
      // Recruiter/admin: profile is always "complete" for display purposes
      setProfileLoading(false);
      setStatsLoading(false);
    }

    fetchJobs();
  }, [user]);

  const fetchJobs = async () => {
    try {
      setJobsLoading(true);
      const response = await jobPostingApi.getRecommendedJobs();
      const jobsData = response.data.matches || response.data || [];
      setRecommendedJobs(jobsData.slice(0, 3));
    } catch {
      setRecommendedJobs([]);
    } finally {
      setJobsLoading(false);
    }
  };

  const handleDismissCompletion = () => {
    const oneWeekFromNow = new Date().getTime() + 7 * 24 * 60 * 60 * 1000;
    localStorage.setItem("completionMessageDismissedUntil", oneWeekFromNow.toString());
    setIsCompletionDismissed(true);
  };

  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    const timeOfDay = hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
    return `Good ${timeOfDay}, ${user?.first_name || user?.full_name?.split(" ")[0] || "there"}!`;
  };

  return (
    <div className="bg-secondary-50 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {user?.role === "professional" && <ExpiredLicenseBanner />}

        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center mb-2 opacity-0 translate-y-[100px] welcome">
                <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center mr-3">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div className="flex items-center gap-3">
                  <h1 className="text-4xl font-bold text-secondary-900">
                    {getWelcomeMessage()}
                  </h1>
                  {verificationData?.verification_percentage === 100 && (
                    <div className="flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full border border-green-200">
                      <BadgeCheck className="w-4 h-4" />
                      <span className="text-sm font-medium">Verified</span>
                    </div>
                  )}
                </div>
              </div>
              <p className="text-secondary-600 text-lg opacity-0 translate-y-[100px] welcome">
                Ready to take the next step in your career journey?
              </p>
            </div>

            <div className="hidden lg:flex items-center gap-4">
              <div className="text-right space-y-2">
                <div>
                  <p className="text-sm text-secondary-500">Member since</p>
                  <p className="font-semibold text-secondary-700">
                    {new Date(user?.date_joined || Date.now()).toLocaleDateString("en-US", {
                      month: "short",
                      year: "numeric",
                    })}
                  </p>
                </div>
                {subscriptionData && (
                  <SubscriptionStatus subscriptionData={subscriptionData} />
                )}
              </div>

              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl flex items-center justify-center relative">
                <span className="text-white font-bold text-lg">
                  {(user?.first_name?.[0] || user?.full_name?.[0] || "U").toUpperCase()}
                </span>
                {verificationData?.is_verified && (
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                    <BadgeCheck className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4 space-y-6">
            {profileLoading ? (
              <ProfileCompletionSkeleton />
            ) : (
              <EnhancedProfileCompletionCard
                profileCompletion={profileCompletion}  // ✅ now from backend via Zustand
                verificationData={verificationData}
                onUpdateProfile={() => navigate("/manage-profile")}
                onViewVerification={() => navigate("/verification")}
              />
            )}

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-primary-50 to-primary-100 p-6 border-b">
                <h3 className="font-bold text-lg text-primary-900 mb-1">Quick Actions</h3>
                <p className="text-primary-600 text-sm">Navigate your career journey</p>
              </div>
              <div className="p-4 space-y-2">
                <QuickActionButton
                  onClick={() => navigate("/job-search")}
                  icon={Briefcase}
                  label="Find Jobs"
                  description="Discover new opportunities"
                  color="blue"
                />
                <QuickActionButton
                  onClick={() => navigate("/my-applications")}
                  icon={FileText}
                  label="My Applications"
                  description="Track your progress"
                  color="green"
                />
                <QuickActionButton
                  onClick={() => navigate("/saved-jobs")}
                  icon={Heart}
                  label="Saved Jobs"
                  description="Review your favorites"
                  color="purple"
                />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <h3 className="font-bold text-lg text-secondary-900 mb-4">Career Insights</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                      <TrendingUp className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm font-medium text-blue-800">Market Trend</span>
                  </div>
                  <span className="text-blue-600 font-semibold">+12%</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center mr-3">
                      <Star className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm font-medium text-green-800">Profile Score</span>
                  </div>
                  <span className="text-green-600 font-semibold">Excellent</span>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {statsLoading ? (
                <><StatCardSkeleton /><StatCardSkeleton /></>
              ) : (
                <>
                  <StatCard
                    icon={Send}
                    value={stats?.total_applications || 0}
                    label="Applications Sent"
                    change={`${stats?.active_applications || 0} are active`}
                    color="green"
                    onClick={() => navigate("/my-applications")}
                  />
                  <StatCard
                    icon={Eye}
                    value={stats?.status_breakdown?.interview || 0}
                    label="Interviews"
                    change="View your schedule"
                    color="blue"
                    onClick={() => navigate("/my-applications")}
                  />
                </>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-primary-50 to-primary-100 p-6 border-b">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold text-primary-900">Recommended For You</h2>
                    <p className="text-primary-600 text-sm mt-1">
                      Curated opportunities matching your profile
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/job-search")}
                    className="border-primary-200 text-primary-700 hover:bg-primary-200"
                  >
                    View All
                  </Button>
                </div>
              </div>
              {jobsLoading ? (
                <div className="p-12 text-center">
                  <div className="relative">
                    <Loader className="w-10 h-10 text-primary-500 animate-spin mx-auto" />
                    <div className="mt-4 text-secondary-600">Finding perfect matches...</div>
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {recommendedJobs.map((job, index) => (
                    <JobCard key={job.job.id} job={job.job} navigate={navigate} index={index} />
                  ))}
                </div>
              )}
            </div>

            <div
              className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
              id="recent-applications"
            >
              <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 border-b">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold text-green-900">Recent Applications</h2>
                    <p className="text-green-600 text-sm mt-1">Track your application status</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/my-applications")}
                    className="border-green-200 text-green-700 hover:bg-green-200"
                  >
                    View All
                  </Button>
                </div>
              </div>
              <div className="divide-y divide-gray-50">
                {statsLoading ? (
                  <div className="p-6 text-center text-sm text-secondary-500">
                    Loading activities...
                  </div>
                ) : stats?.recent_activity?.length > 0 ? (
                  stats.recent_activity.map((app, index) => (
                    <ApplicationCard
                      key={`${app.application_id}-${app.timestamp}-${index}`}
                      application={app}
                      navigate={navigate}
                    />
                  ))
                ) : (
                  <div className="p-6 text-center text-sm text-secondary-500">
                    No recent application activity.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;